import React from 'react';
import { Box, CircularProgress, Link, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import type { EntitlementSnapshot } from '../api/subscription';

export type AiMonthlyCreditsSummaryVariant = 'intake_strip' | 'dialog' | 'drawer_hint';

type Props = {
    variant: AiMonthlyCreditsSummaryVariant;
    /** True while the first bootstrap load is in flight (no cached bootstrap yet). */
    bootstrapBlockingLoad: boolean;
    bootstrapError: string | null;
    entitlements: EntitlementSnapshot | null | undefined;
};

const formatReset = (reset?: string | null) => {
    if (!reset) return null;
    const d = new Date(reset);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString();
};

const BillingLink: React.FC<{ inline?: boolean }> = ({ inline }) => (
    <Link
        component={RouterLink}
        to="/app/billing"
        underline="hover"
        sx={{ fontWeight: inline ? 600 : 500, color: 'var(--color-accent)' }}
    >
        Billing
    </Link>
);

/**
 * Monthly AI credit snapshot for Intake, confirm dialogs, and Workbench drawer (Story 6.1).
 * Stays aligned with Billing numbers; no fabricated counts when loading or unavailable.
 */
export const AiMonthlyCreditsSummary: React.FC<Props> = ({
    variant,
    bootstrapBlockingLoad,
    bootstrapError,
    entitlements,
}) => {
    const compact = variant === 'drawer_hint';
    const bodyVariant = compact ? 'caption' : 'body2';

    if (bootstrapError) {
        return (
            <Box data-testid="ai-credits-unavailable">
                <Typography variant={bodyVariant} sx={{ color: 'var(--color-text-secondary)' }}>
                    We couldn&apos;t load usage right now. Check{' '}
                    <BillingLink inline /> for plan details, or try again shortly.
                </Typography>
            </Box>
        );
    }

    if (bootstrapBlockingLoad) {
        return (
            <Box
                data-testid="ai-credits-loading"
                sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: variant === 'intake_strip' ? 'center' : 'flex-start' }}
            >
                <CircularProgress size={compact ? 14 : 18} sx={{ color: 'var(--color-accent)' }} />
                <Typography variant={bodyVariant} sx={{ color: 'var(--color-text-muted)' }}>
                    Loading usage…
                </Typography>
            </Box>
        );
    }

    if (!entitlements) {
        return (
            <Box data-testid="ai-credits-unavailable">
                <Typography variant={bodyVariant} sx={{ color: 'var(--color-text-secondary)' }}>
                    Usage isn&apos;t available here yet. Open <BillingLink inline /> to see your plan and limits.
                </Typography>
            </Box>
        );
    }

    const included = entitlements.included_ai_credits_monthly;
    const remaining = entitlements.ai_credits_remaining;
    const used = entitlements.ai_credits_used;
    const resetLabel = formatReset(entitlements.reset_date ?? entitlements.current_period_end);

    const primary =
        variant === 'intake_strip' ? (
            <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>
                    {remaining} of {included} monthly AI credits remaining
                </Typography>
                {used > 0 && (
                    <Typography variant="body2" sx={{ fontWeight: 500, color: 'var(--color-text-muted)', mt: 0.5 }}>
                        {used} used this cycle
                    </Typography>
                )}
            </Box>
        ) : (
            <Typography variant={bodyVariant} sx={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>
                {remaining} / {included} monthly AI credits remaining
                {compact ? '' : ` · ${used} used`}
            </Typography>
        );

    return (
        <Box data-testid="ai-credits-loaded">
            {primary}
            {resetLabel && (
                <Typography
                    variant={compact ? 'caption' : 'body2'}
                    sx={{
                        color: 'var(--color-text-muted)',
                        mt: compact ? 0.25 : 0.5,
                        textAlign: variant === 'intake_strip' ? 'center' : 'left',
                        display: 'block',
                    }}
                >
                    Resets {resetLabel}
                </Typography>
            )}
        </Box>
    );
};

export default AiMonthlyCreditsSummary;
